import { defineStore } from 'pinia';
import { computed, reactive, ref, unref, watch } from 'vue';
import type { Vector3 } from '@kitware/vtk.js/types';
import { vec3 } from 'gl-matrix';

import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { Manifest, StateFile } from '@/src/io/state-file/schema';
import type {
  CurvedMPRParams,
  CurvedMPRSlabMode,
  DentalArch,
} from '@/src/io/state-file/schema';
import { usePycadUiStore } from '@/src/store/pycad-ui';
import useViewSliceStore from '@/src/store/view-configs/slicing';
import {
  ArcFrame,
  buildArcFrames,
  buildArcLengthLUT,
  interpolateCatmullRom,
} from '@/src/utils/dentalCurve';
import {
  getVolume2DSlicePlaneInWorld,
  projectPointOntoPlane,
} from '@/src/utils/manipulators';
import type { LPSAxisDir } from '@/src/types/lps';
import { useImplantPlanningStore } from '@/src/store/tools/implantPlanning';
import vtkCurvedMPRWidget from '@/src/vtk/CurvedMPRWidget';
import type { CurvedMPRBehaviorCallbacks } from '@/src/vtk/CurvedMPRWidget/behavior';

/** Must match CurvedMPRAxialEditor / CurvedMPRWidgetMount viewDirection. */
const CURVED_MPR_AXIAL_VIEW_DIRECTION: LPSAxisDir = 'Inferior';

export const DEFAULT_CURVED_MPR_PARAMS: CurvedMPRParams = {
  thicknessMm: 1,
  spacingMm: 1,
  verticalExtentMm: 80,
  innerOffsetMm: 17.5,
  outerOffsetMm: 17.5,
  crossSectionWidthMm: 30,
  csTileCount: 3,
  // MIP is the standard dental panoramic look: it emphasises bone and
  // enamel and avoids the soft "mean slab" blur that makes the panoramic
  // look out of focus. The user can switch to mean / centerline in the
  // params panel if they prefer a different rendering.
  slabMode: 'mip',
};

const SAMPLES_PER_SEGMENT = 24;

type ArchPoints = Vector3[];

export const useCurvedMPRStore = defineStore('curvedMPR', () => {
  // VTK widget factory used by the axial editor to capture mouse events on
  // the axial slice plane. The store owns the factory (and thus its lifetime)
  // so all 2D views that mount it share the same instance — same pattern as
  // the Crosshairs store.
  const factory = vtkCurvedMPRWidget.newInstance();

  const arches = reactive<Record<DentalArch, ArchPoints>>({
    mandible: [],
    maxilla: [],
  });
  const activeArch = ref<DentalArch>('mandible');
  const csIndex = ref(0);
  const params = reactive<CurvedMPRParams>({ ...DEFAULT_CURVED_MPR_PARAMS });
  /** Bumped on every spline / params change so debounced consumers can react. */
  const generation = ref(0);
  /**
   * Whether the panoramic worker is mid-flight. The panoramic view publishes
   * this so the workspace's progress overlay knows when the *first* sample
   * has completed.
   */
  const isPanoramicSampling = ref(false);
  /** Bumped after every successful panoramic sample (worker round-trip). */
  const panoramicGeneration = ref(0);
  /**
   * Whether panoramic and cross-section views have been generated at least
   * once this session (via "Generate panoramic"). Until true, placing spline
   * points does not mount cross-section VTK views or run the panoramic worker.
   * After true, spline and param edits auto-refresh those views.
   */
  const panoramicGenerated = ref(false);

  /** Shared view-store ID for panoramic + cross-section window/level. */
  const derivedWlViewId = ref<string | null>(null);
  /** Registered Curved MPR axial 2D view (for slice plane projection). */
  const axialViewId = ref<string | null>(null);
  /** Show arch spline, control handles, and inner/outer envelope in the axial view. */
  const axialArchCurvesVisible = ref(true);

  function setDerivedWlViewId(id: string | null) {
    derivedWlViewId.value = id;
  }

  function setAxialViewId(id: string | null) {
    axialViewId.value = id;
  }

  function toggleAxialArchCurves() {
    axialArchCurvesVisible.value = !axialArchCurvesVisible.value;
  }

  /**
   * Re-project control points onto the axial slice currently shown in the
   * editor. Points keep their in-plane (X/Y) world position but move to the
   * scrolled slice depth so regenerate uses the arch on the visible slice.
   */
  function projectActiveArchOntoCurrentAxialSlice() {
    const viewId = axialViewId.value;
    const imageId = currentImageID.value;
    const meta = unref(currentImageMetadata);
    if (!viewId || !imageId || !meta) return;

    const sliceCfg = useViewSliceStore().getConfig(viewId, imageId);
    if (!sliceCfg) return;

    const { origin, normal } = getVolume2DSlicePlaneInWorld(
      CURVED_MPR_AXIAL_VIEW_DIRECTION,
      sliceCfg.slice,
      meta
    );

    const pts = arches[activeArch.value];
    for (let i = 0; i < pts.length; i++) {
      const p = projectPointOntoPlane(pts[i], origin, normal);
      pts[i] = [p[0], p[1], p[2]];
    }
  }

  // Track the current image so we clear curve state when the user opens a
  // different dataset (the curve is in world LPS, so it makes no sense to
  // keep stale points across image switches).
  const { currentImageID, currentImageMetadata } = useCurrentImage('global');
  let lastImageID: string | null | undefined = undefined;

  function bumpGeneration() {
    generation.value = (generation.value + 1) | 0;
  }

  /**
   * Called by the workspace component when it mounts for a new session.
   */
  function beginFreshSession() {
    panoramicGenerated.value = false;
  }

  /**
   * First-time generate or manual regenerate from the params panel.
   * Loading flag is set synchronously; heavy work is deferred so the UI can
   * paint spinners before mounting cross-section VTK views / panoramic worker.
   */
  function requestRegenerate() {
    isPanoramicSampling.value = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        projectActiveArchOntoCurrentAxialSlice();
        panoramicGenerated.value = true;
        bumpGeneration();
      });
    });
  }

  function setActiveArch(arch: DentalArch) {
    if (activeArch.value === arch) return;
    activeArch.value = arch;
    csIndex.value = 0;
    bumpGeneration();
  }

  function getActivePoints(): ArchPoints {
    return arches[activeArch.value];
  }

  function addPoint(point: Vector3) {
    arches[activeArch.value].push([point[0], point[1], point[2]]);
    bumpGeneration();
  }

  function movePoint(index: number, point: Vector3) {
    const list = arches[activeArch.value];
    if (index < 0 || index >= list.length) return;
    list[index] = [point[0], point[1], point[2]];
    bumpGeneration();
  }

  function removePoint(index: number) {
    const list = arches[activeArch.value];
    if (index < 0 || index >= list.length) return;
    list.splice(index, 1);
    csIndex.value = 0;
    bumpGeneration();
  }

  function clearActiveArch() {
    arches[activeArch.value] = [];
    csIndex.value = 0;
    bumpGeneration();
  }

  function clearAll() {
    arches.mandible = [];
    arches.maxilla = [];
    csIndex.value = 0;
    panoramicGenerated.value = false;
    useImplantPlanningStore().clearAll();
    bumpGeneration();
  }

  function setParams(
    next: Partial<CurvedMPRParams>,
    options?: { bump?: boolean }
  ) {
    Object.assign(params, next);
    if (options?.bump !== false) {
      bumpGeneration();
    }
  }

  /** Commit envelope edits after an axial drag (panoramic regen if generated). */
  function commitEnvelopeDrag() {
    bumpGeneration();
  }

  function setSlabMode(mode: CurvedMPRSlabMode) {
    params.slabMode = mode;
    bumpGeneration();
  }

  function clampCsIndex(i: number) {
    const max = Math.max(0, csCount.value - 1);
    return Math.max(0, Math.min(max, Math.round(i)));
  }

  let pendingCsIndex: number | null = null;
  let csIndexRaf: number | null = null;

  function applyCsIndex(i: number) {
    csIndex.value = clampCsIndex(i);
  }

  function cancelCsIndexRaf() {
    if (csIndexRaf != null) {
      cancelAnimationFrame(csIndexRaf);
      csIndexRaf = null;
    }
  }

  /**
   * Update the active cross-section index. By default commits at most once per
   * animation frame so slider scrubbing does not flood VTK reslices.
   */
  function setCsIndex(i: number, options?: { immediate?: boolean }) {
    const clamped = clampCsIndex(i);
    if (options?.immediate) {
      cancelCsIndexRaf();
      pendingCsIndex = null;
      applyCsIndex(clamped);
      return;
    }
    pendingCsIndex = clamped;
    if (csIndexRaf != null) return;
    csIndexRaf = requestAnimationFrame(() => {
      csIndexRaf = null;
      if (pendingCsIndex != null) {
        applyCsIndex(pendingCsIndex);
        pendingCsIndex = null;
      }
    });
  }

  /** Apply any pending throttled csIndex (e.g. on slider release). */
  function flushCsIndex() {
    if (pendingCsIndex == null) return;
    const next = pendingCsIndex;
    cancelCsIndexRaf();
    pendingCsIndex = null;
    applyCsIndex(next);
  }

  function setPanoramicSampling(state: boolean) {
    isPanoramicSampling.value = state;
    if (!state) {
      panoramicGeneration.value = (panoramicGeneration.value + 1) | 0;
    }
  }

  // The widget sits in vtk-land but it can't reach Vue/Pinia directly, so
  // we hand its behavior a small POJO with the methods it needs. The factory
  // gets a copy too in case any caller wants to inspect; the authoritative
  // copy lives on each *view widget* (set when the widget mounts), since
  // vtk's AbstractWidgetFactory doesn't propagate factory model fields down
  // to the per-view widgets.
  const widgetCallbacks: CurvedMPRBehaviorCallbacks = {
    getPoints: () => arches[activeArch.value],
    isInputEnabled: () => {
      const ui = usePycadUiStore();
      const implantStore = useImplantPlanningStore();
      return (
        ui.curvedMPRWorkspaceActive &&
        axialArchCurvesVisible.value &&
        !implantStore.placementMode &&
        !implantStore.isManipulating
      );
    },
    addPoint,
    movePoint,
    removePoint,
  };
  factory.setCallbacks(widgetCallbacks);

  // -- derived geometry --
  const sampledCurve = computed<Vector3[]>(() => {
    const pts = arches[activeArch.value];
    if (pts.length < 2) return pts.map((p) => [p[0], p[1], p[2]]);
    return interpolateCatmullRom(pts, SAMPLES_PER_SEGMENT);
  });

  const arcLengthLUT = computed(() => buildArcLengthLUT(sampledCurve.value));

  // Up direction for the Frenet frames: the volume's Superior LPS axis in
  // world coordinates. Falls back to +Z for an empty metadata.
  const globalUp = computed<Vector3>(() => {
    const meta = unref(currentImageMetadata);
    const sup = meta?.lpsOrientation?.Superior;
    if (sup) return [sup[0], sup[1], sup[2]];
    return [0, 0, 1];
  });

  const frames = computed<ArcFrame[]>(() => {
    if (sampledCurve.value.length < 2) return [];
    return buildArcFrames(
      sampledCurve.value,
      arcLengthLUT.value,
      globalUp.value
    );
  });

  const arcLengthMm = computed(() => arcLengthLUT.value.totalLength);

  const csCount = computed(() => {
    const total = arcLengthMm.value;
    if (total <= 0) return 0;
    const spacing = Math.max(0.1, params.spacingMm);
    return Math.max(1, Math.floor(total / spacing) + 1);
  });

  /**
   * The arc-length position (mm) of the cross-section identified by csIndex.
   * Evenly spaced from 0 to arcLengthMm.
   */
  const csArc = computed(() => {
    if (csCount.value <= 1) return 0;
    return Math.min(
      arcLengthMm.value,
      csIndex.value * Math.max(0.1, params.spacingMm)
    );
  });

  // -- guards & resets --
  watch(
    () => currentImageID.value,
    (id) => {
      if (lastImageID === undefined) {
        lastImageID = id;
        return;
      }
      if (id !== lastImageID) {
        clearAll();
        lastImageID = id;
      }
    },
    { immediate: true }
  );

  function activateTool() {
    return true;
  }

  function deactivateTool() {
    // No-op: the widget is owned by the workspace components and torn down
    // when the workspace unmounts, not when the tool deactivates.
  }

  function getWidgetFactory() {
    return factory;
  }

  function getWidgetCallbacks() {
    return widgetCallbacks;
  }

  // -- serialize / deserialize --
  function serialize(state: StateFile) {
    const ui = usePycadUiStore();
    const slot = state.manifest.tools?.curvedMPR;
    if (!slot) return;

    slot.workspaceActive = ui.curvedMPRWorkspaceActive;
    slot.activeArch = activeArch.value;
    slot.arches = {
      mandible: arches.mandible.length
        ? { points: arches.mandible.map((p) => [p[0], p[1], p[2]]) }
        : undefined,
      maxilla: arches.maxilla.length
        ? { points: arches.maxilla.map((p) => [p[0], p[1], p[2]]) }
        : undefined,
    };
    slot.params = { ...params };
    slot.csIndex = csIndex.value;
    slot.panoramicGenerated = panoramicGenerated.value;
    const implants = useImplantPlanningStore().exportForSerialize();
    slot.implants = implants.length ? implants : undefined;
  }

  function deserialize(manifest: Manifest) {
    const slot = manifest.tools?.curvedMPR;
    if (!slot) return;

    if (slot.params) {
      Object.assign(params, DEFAULT_CURVED_MPR_PARAMS, slot.params);
      // Migrate legacy panoramic slab (symmetric thicknessMm) to envelope.
      const saved = slot.params;
      if (
        saved.innerOffsetMm == null &&
        saved.outerOffsetMm == null &&
        saved.thicknessMm > 0
      ) {
        const half = saved.thicknessMm / 2;
        params.innerOffsetMm = half;
        params.outerOffsetMm = half;
      }
    }
    if (slot.activeArch) {
      activeArch.value = slot.activeArch;
    }
    arches.mandible = (slot.arches?.mandible?.points ?? []).map(
      (p) => [p[0], p[1], p[2]] as Vector3
    );
    arches.maxilla = (slot.arches?.maxilla?.points ?? []).map(
      (p) => [p[0], p[1], p[2]] as Vector3
    );
    csIndex.value = slot.csIndex ?? 0;
    panoramicGenerated.value = slot.panoramicGenerated ?? false;
    if (slot.implants?.length) {
      useImplantPlanningStore().importFromDeserialize(slot.implants);
    }
    if (panoramicGenerated.value) {
      bumpGeneration();
    }

    if (slot.workspaceActive) {
      const ui = usePycadUiStore();
      ui.setCurvedMPRWorkspaceActive(true);
    }

    // The image-change watcher above would otherwise immediately wipe the
    // points we just restored, since lastImageID hasn't been initialized for
    // this freshly-loaded session.
    lastImageID = currentImageID.value;
    bumpGeneration();
  }

  return {
    arches,
    activeArch,
    csIndex,
    params,
    generation,
    isPanoramicSampling,
    panoramicGeneration,
    panoramicGenerated,
    derivedWlViewId,
    setDerivedWlViewId,
    axialViewId,
    setAxialViewId,
    axialArchCurvesVisible,
    toggleAxialArchCurves,
    setPanoramicSampling,
    beginFreshSession,
    requestRegenerate,
    sampledCurve,
    frames,
    arcLengthLUT,
    arcLengthMm,
    csCount,
    csArc,
    globalUp,
    setActiveArch,
    getActivePoints,
    addPoint,
    movePoint,
    removePoint,
    clearActiveArch,
    clearAll,
    setParams,
    commitEnvelopeDrag,
    setSlabMode,
    setCsIndex,
    flushCsIndex,
    activateTool,
    deactivateTool,
    getWidgetFactory,
    getWidgetCallbacks,
    serialize,
    deserialize,
  };
});

// Helper for vec3 math reuse elsewhere
export function distance(a: Vector3, b: Vector3) {
  const v = vec3.create();
  vec3.subtract(v, a, b);
  return vec3.length(v);
}
